import { CaseStatus } from '@debbie/common/dist/cases/index.js'
import { getContactPerson } from '@debbie/common/dist/customers/index.js'
import {
  EmbedPlacement,
  getEmbedAreas,
} from '@debbie/common/dist/embeds/index.js'
import { FeatureFlag } from '@debbie/common/dist/feature-flags/index.js'
import { CountryCode, Entity } from '@debbie/common/dist/index.js'
import { TodoType } from '@debbie/common/dist/workflows/index.js'
import { useCaseVouchersByCase } from '@debbie/cortex/dist/case-vouchers/index.js'
import { useCase } from '@debbie/cortex/dist/cases/index.js'
import { useConnectMappingEntityMappings } from '@debbie/cortex/dist/connect//index.js'
import { useCreditor } from '@debbie/cortex/dist/creditors/index.js'
import { useCustomer } from '@debbie/cortex/dist/customers/index.js'
import { c, h } from '@debbie/cortex/dist/index.js'
import { useInteractionsByReference } from '@debbie/cortex/dist/interactions/index.js'
import { RootState } from '@debbie/cortex/dist/reducer.js'
import { useTenant } from '@debbie/cortex/dist/tenant/index.js'
import { useUpdatesByRefId } from '@debbie/cortex/dist/updates/index.js'
import AssociatedCases from '@debbie/iris/dist/components/AssociatedCases/AssociatedCases.js'
import CreditorNotes from '@debbie/iris/dist/components/CreditorInfo/CreditorNotes.js'
import Documents from '@debbie/iris/dist/components/documents/Documents.js'
import { Col, Row } from '@debbie/iris/dist/components/Grid/index.js'
import CaseHistory from '@debbie/iris/dist/components/Hist/CaseHistory.js'
import { caseHistoryFocusSchema } from '@debbie/iris/dist/components/Hist/focus.js'
import {
  AgentProposals,
  Button,
  CaseInfo,
  CasePauseStatus,
  CopyToClipboard,
  EmbedsFrame,
  Modal,
  Panel,
  Properties,
  ResponsiblePreview,
  SmallHeader,
  SubscribedToCosmo,
} from '@debbie/iris/dist/components/index.js'
import CreateTribunalCase from '@debbie/iris/dist/components/tribunal/CreateTribunalCase/index.js'
import Tribunal from '@debbie/iris/dist/components/tribunal/Tribunal.js'
import { useVariation } from '@debbie/iris/dist/feature-flags/index.js'
import { useUrlState } from '@debbie/iris/dist/utils/url-state.js'
import React, { useEffect, useState } from 'react'
import { useSelector } from 'react-redux'

import NewInteraction from '../../components/NewInteraction.js'
import { CaseEconomy, CaseHeader } from './index.js'

type Props = {
  caseId: string
  outboundMode?: boolean
}

const CaseSummary = (props: Props) => {
  const tenant = useTenant()

  const [historyFocus] = useUrlState(
    'focus',
    caseHistoryFocusSchema.nullable(),
    null,
  )
  const [showNewUpdate, setShowNewUpdate] = useState(false)
  const [tribunalModalOpen, setTribunalModalOpen] = useState(false)

  const lang = useSelector((s: RootState) => s.lang)
  const singleCaseLoader = useCase(props.caseId)
  const singleCase = h.hasData(singleCaseLoader) ? singleCaseLoader.data : null

  const customerLoader = useCustomer(singleCase?.customerId)
  const customer = h.hasData(customerLoader) ? customerLoader.data : null

  const caseInteractionsLoadable = useInteractionsByReference(
    Entity.Case,
    singleCase?.caseId,
    true,
  )
  const caseInteractions = h.hasData(caseInteractionsLoadable)
    ? caseInteractionsLoadable.data
    : null

  const caseVouchers_ = useCaseVouchersByCase(singleCase?.caseId)
  const caseVouchers = h.hasData(caseVouchers_) ? caseVouchers_.data : null

  const creditorLoadable = useCreditor(singleCase?.creditorId)
  const creditor = h.hasData(creditorLoadable) ? creditorLoadable.data : null

  const updatesLoadable = useUpdatesByRefId(singleCase?.caseId)
  const updates = h.hasData(updatesLoadable) ? updatesLoadable.data : null

  const usingConnect = !!updates?.find((u) => {
    return u.procedure?.find((p) => p.type === TodoType.ConnectToDebtCollector)
  })

  const entityMappings = useConnectMappingEntityMappings(singleCase?.caseId, {
    enabled: usingConnect,
  })
  const connectMappings = h.hasData(entityMappings) ? entityMappings.data : {}

  const embedAreas = tenant
    ? getEmbedAreas(tenant.embeds, EmbedPlacement.CaseworkerWebCasePage)
    : []

  const contactPerson =
    customer && singleCase && getContactPerson(customer, singleCase)

  const hasTribunal = useVariation(FeatureFlag.TRIBUNAL)
  const hasAIEnabled = useVariation(FeatureFlag.AISuggestions)

  return (
    <Row className="ui-test-c9bb9cb0">
      {singleCase?.associationId && (
        <Col size="md" width="12">
          <AssociatedCases
            singleCase={singleCase}
            associationId={singleCase.associationId}
          />
        </Col>
      )}
      {singleCase && customer && (
        <CaseHeader
          case={singleCase}
          customer={customer}
          id={`${singleCase?.id}`}
          setShowNewUpdate={setShowNewUpdate}
          showNewUpdate={showNewUpdate}
        />
      )}
      <Col size="md" width="12">
        {singleCase && (
          <CasePauseStatus
            case={singleCase}
            setShowNewUpdate={setShowNewUpdate}
            showNewUpdate={showNewUpdate}
          />
        )}
      </Col>
      <Col size="md" width="5">
        <Panel
          header={
            <>
              <CopyToClipboard text={singleCase?.caseId} hideIcon>
                {lang.cases.caseInfo}
              </CopyToClipboard>
              <small style={{ float: 'right', marginTop: '-7px' }}>
                {singleCase &&
                  [CaseStatus.PENDING, CaseStatus.VERIFIED].includes(
                    singleCase.status,
                  ) && <SubscribedToCosmo />}
              </small>
            </>
          }
          bodyStyle={{
            minHeight: '350px',
          }}
        >
          <CaseInfo caseId={props.caseId} />
          <br />
          {creditor && (
            <div style={{ marginBottom: '-20px' }}>
              <SmallHeader>
                {lang.creditors.creditor} {lang.creditors.mandates}
              </SmallHeader>
              <CreditorNotes creditor={creditor} />
            </div>
          )}
          {singleCase &&
            singleCase.responsibleReferenceId &&
            singleCase.responsibleType && (
              <div style={{ display: 'inline-flex', alignItems: 'center' }}>
                <p style={{ marginRight: '5px' }}>{lang.cases.responsible}:</p>
                <ResponsiblePreview
                  responsibleReferenceId={singleCase.responsibleReferenceId}
                  responsibleType={singleCase.responsibleType}
                />
              </div>
            )}
        </Panel>

        <CaseEconomy case={singleCase} id={props.caseId} />
        {embedAreas &&
          singleCase &&
          embedAreas.map((embedArea) => {
            if (embedArea.placement !== EmbedPlacement.CaseworkerWebCasePage) {
              return null
            }

            return (
              <Panel
                header={embedArea.title}
                bodyStyle={{ overflow: 'hidden' }}
                key={embedArea.embedId}
              >
                <div
                  style={{
                    margin: '-20px -20px -25px -20px',
                  }}
                >
                  <EmbedsFrame
                    id={embedArea.embedId}
                    height={embedArea.details.height}
                    context={{
                      caseId: singleCase.caseId,
                    }}
                  />
                </div>
              </Panel>
            )
          })}
        {singleCase && caseInteractions && (
          <Documents
            case={singleCase}
            name={contactPerson?.details?.name}
            role={
              contactPerson?.relation === 'alternative-contact'
                ? lang.debtors.alternativeContact
                : lang.debtors.debtor
            }
            entityId={singleCase.caseId}
            entityType={Entity.Case}
            connectMappings={connectMappings}
          />
        )}
        {tribunalModalOpen && singleCase && (
          <Modal
            onToggle={() => setTribunalModalOpen(!tribunalModalOpen)}
            header={lang.tribunal.tribunal}
          >
            <CreateTribunalCase
              caseSeqId={singleCase.id}
              caseId={singleCase.caseId}
              tenantId={singleCase.tenantId}
              onCreated={() => setTribunalModalOpen(false)}
            />
          </Modal>
        )}
        {hasTribunal &&
          singleCase &&
          caseVouchers &&
          tenant &&
          ([CountryCode.DK, CountryCode.GL] as string[]).includes(
            tenant.countryCode,
          ) && (
            <Panel
              header={
                <span>
                  {lang.tribunal.tribunal}{' '}
                  <Button
                    style={{ float: 'right' }}
                    onClick={() => setTribunalModalOpen(!tribunalModalOpen)}
                    size="mini"
                    theme="secondary"
                    className="ui-test-2cc0b2a5"
                  >
                    {lang.tribunal.addTribunalCase}
                  </Button>
                </span>
              }
            >
              <Tribunal
                case={singleCase}
                tenantId={singleCase.tenantId}
                caseVouchers={caseVouchers}
                creditorId={singleCase.creditorId}
              />
            </Panel>
          )}

        {singleCase && (
          <Panel header={lang.properties.properties}>
            <Properties
              propRef={Entity.Case}
              propRefId={singleCase.caseId}
              caseGroupId={singleCase.caseGroup?.id ?? null}
            />
          </Panel>
        )}
      </Col>
      <Col size="md" width="7">
        <Row>
          {hasAIEnabled && singleCase && (
            <Col size="md" width="12">
              <AgentProposals caseId={singleCase.caseId} />
            </Col>
          )}
          <Col size="md" width="12">
            {singleCase && (
              <NewInteraction
                referenceId={singleCase.caseId}
                referenceType={Entity.Case}
                key={`${singleCase.caseId}-${contactPerson?.userId}`}
                outboundMode={props.outboundMode}
              />
            )}
          </Col>

          <Col size="md" width="12">
            <br />
            {singleCase && (
              <CaseHistory caseId={props.caseId} focus={historyFocus} />
            )}
          </Col>
        </Row>
      </Col>
    </Row>
  )
}

export default CaseSummary
